import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastInfo } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { useServerErrorMessage } from '@/components/organisms/FishGroupForms';
import type { TimelineFishGroup } from '@/domain/fishGroups';
import { useGetStockingLineage, useStockingAssignment } from '@/domain/stockingCycles';

import { removeOffer } from '../../helpers/stockingAssignment';
import type { BoardCycle } from '../../types';
import ModalFormActions from '../ModalFormActions/ModalFormActions';
import ActionDialogTitle from '../SiteActionDialog/ActionDialogTitle';

import LineageGroups from './LineageGroups';
import LineageRefusal from './LineageRefusal';

import styles from './StockingAssignment.module.scss';

interface RemoveFromStockingModalProps {
    clientId: number;
    locationId: number;
    /** Real names: the dialog never mounts in demo mode. */
    group: TimelineFishGroup;
    /** The stocking the group is in. */
    cycle: BoardCycle;
    cycles: BoardCycle[];
    onClose: () => void;
    /** After the lineage left the stocking: the board shows Unassigned, where it now is. */
    onDone: () => void;
}

/** "Remove from stocking": the group's whole lineage goes back to Unassigned, once confirmed. */
export default function RemoveFromStockingModal(props: RemoveFromStockingModalProps) {
    const { clientId, locationId, group, cycle, cycles, onClose, onDone } = props;
    const { t } = useTranslation('fishGroups');
    const lineage = useGetStockingLineage(clientId, locationId, group.id);
    const actions = useStockingAssignment(clientId, locationId);
    const serverError = useServerErrorMessage(actions.error);
    const offer = lineage.data ? removeOffer(lineage.data, cycle.id, cycles) : null;

    const close = () => {
        if (actions.isLoading) return;
        actions.reset();
        onClose();
    };

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (offer?.kind !== 'ok') return;
        const result = await actions.unassign(group.id, cycle.id).catch(() => null);
        if (!result) return;
        ToastInfo({
            content: t('stockingAssignment.remove.successToast', { count: result.movedCount }),
            status: 'success',
            icon: 'success',
            options: { toastId: 'stocking-unassignment-success', autoClose: 5000 },
        });
        actions.reset();
        onDone();
    };

    const body = (
        <form className={styles.form} onSubmit={submit} noValidate>
            <p className={styles.hint}>
                {t('stockingAssignment.remove.description', { name: cycle.name })}
            </p>
            <LineageGroups
                lineage={lineage}
                cycleName={(id) => cycles.find((candidate) => candidate.id === id)?.name ?? ''}
            />
            <LineageRefusal offer={offer} />
            {serverError && <p className={styles.serverError}>{serverError}</p>}
            <ModalFormActions
                cancelLabel={t('actions.cancel')}
                submitLabel={t('stockingAssignment.remove.submit')}
                isSubmitting={actions.isLoading}
                submitDisabled={offer?.kind !== 'ok'}
                onCancel={close}
            />
        </form>
    );

    return (
        <ModalComponent
            isOpen
            size="lg"
            onClose={close}
            title={
                <ActionDialogTitle
                    kind="fishGroup"
                    title={t('stockingAssignment.remove.title')}
                    groupName={group.name}
                    penName={null}
                    bioscopeIds={[]}
                />
            }
            body={body}
            backdrop="static"
        />
    );
}
